/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MergeKey } from '../models/MergeKey';
import type { PaginateJsonSchema } from '../models/PaginateJsonSchema';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MergeKeysService {

    /**
     * List Created Merge Keys
     * Returns merge keys the admin has created for the account (rows in the
     * merge_keys table). Optionally filtered by key (case-insensitive
     * substring) and paginated.
     *
     * @param accountExternalId Account External ID
     * @param query Case-insensitive substring filter on key
     * @param page Page number (when paginating)
     * @param perPage Page size (when paginating)
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiMergeKeys(
        accountExternalId: string,
        query?: string,
        page?: number,
        perPage?: number,
    ): CancelablePromise<{
        merge_keys: Array<MergeKey>;
        pagination?: PaginateJsonSchema;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/merge_keys/{account_external_id}',
            path: {
                'account_external_id': accountExternalId,
            },
            query: {
                'query': query,
                'page': page,
                'per_page': perPage,
            },
            errors: {
                404: `account not found`,
            },
        });
    }

    /**
     * Create Merge Key
     * Creates a MergeKey row. The :key must be one returned by GET /available.
     * @param accountExternalId Account External ID
     * @param requestBody
     * @returns any created
     * @throws ApiError
     */
    public static postInternalApiMergeKeys(
        accountExternalId: string,
        requestBody?: {
            /**
             * Merge-key form, e.g. OPENING_NAME
             */
            key: string;
            translatable?: boolean;
        },
    ): CancelablePromise<{
        merge_key: MergeKey;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/merge_keys/{account_external_id}',
            path: {
                'account_external_id': accountExternalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `account not found`,
                422: `key not available for account`,
            },
        });
    }

    /**
     * List Available Merge Keys
     * Returns merge keys available to the account that have NOT yet been
     * created (no MergeKey row). Used to power the "add merge key" picker.
     * Sources: hardcoded keys, APPLICANT_DATA_* (from DataKeys),
     * CUSTOMER_ATTRIBUTES_* (from AttributeTypes).
     *
     * @param accountExternalId Account External ID
     * @param query Case-insensitive substring filter on key
     * @param page Page number (when paginating)
     * @param perPage Page size (when paginating)
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiMergeKeysAvailable(
        accountExternalId: string,
        query?: string,
        page?: number,
        perPage?: number,
    ): CancelablePromise<{
        merge_keys: Array<string>;
        pagination: PaginateJsonSchema;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/merge_keys/{account_external_id}/available',
            path: {
                'account_external_id': accountExternalId,
            },
            query: {
                'query': query,
                'page': page,
                'per_page': perPage,
            },
            errors: {
                404: `account not found`,
            },
        });
    }

    /**
     * Update Merge Key
     * Updates the row in place. Both :key and :translatable are optional; identity is the row, so :key can be renamed (within the keys available to the account) without losing settings.
     * @param accountExternalId Account External ID
     * @param id Merge Key external_id (UUID)
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiMergeKeys(
        accountExternalId: string,
        id: string,
        requestBody?: {
            /**
             * New merge-key form (optional)
             */
            key?: string;
            /**
             * Toggle translation gating (optional)
             */
            translatable?: boolean;
        },
    ): CancelablePromise<{
        merge_key: MergeKey;
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/merge_keys/{account_external_id}/{id}',
            path: {
                'account_external_id': accountExternalId,
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `merge key not found`,
                422: `key not available for account`,
            },
        });
    }

    /**
     * Delete Merge Key
     * @param accountExternalId Account External ID
     * @param id Merge Key external_id (UUID)
     * @returns void
     * @throws ApiError
     */
    public static deleteInternalApiMergeKeys(
        accountExternalId: string,
        id: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/merge_keys/{account_external_id}/{id}',
            path: {
                'account_external_id': accountExternalId,
                'id': id,
            },
            errors: {
                404: `merge key not found`,
            },
        });
    }

}